import {
  Activity,
  ArrowLeft,
  MoreVertical,
  Pencil,
  Sparkles,
  Star,
  Trash2,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ErrorState } from "@/components/feedback/ErrorState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingState } from "@/components/feedback/LoadingState";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { RateAgentDialog } from "@/features/agents/components/RateAgentDialog";
import { useAgent, useAgentRatings } from "@/features/agents/hooks/useAgents";
import { useAuth } from "@/auth/useAuth";
import { TeamChatView } from "@/features/team-sessions/components/TeamChatView";
import { ChatSidePanel } from "@/features/threads/components/ChatSidePanel";
import { ChatStream } from "@/features/threads/components/ChatStream";
import {
  useDeleteThread,
  useThread,
  useUpdateThread,
} from "@/features/threads/hooks/useThreads";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/stores/uiStore";

export function ChatPage() {
  const { t } = useTranslation("threads");
  const { t: tc } = useTranslation("common");
  const { threadId = "" } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Modo draft: rota /threads/new?agent_id=<id>. A thread so e criada
  // quando o usuario envia a primeira mensagem (ChatStream cuida disso).
  // A rota estatica ``/threads/new`` nao alimenta ``useParams`` (so o
  // template ``/threads/:threadId`` faz), entao detectamos via pathname.
  const isDraft =
    location.pathname === "/threads/new" || threadId === "new";
  const draftAgentId = searchParams.get("agent_id") ?? "";

  const threadQuery = useThread(isDraft ? "" : threadId);
  const draftAgent = useAgent(isDraft ? draftAgentId : "");
  const del = useDeleteThread();
  const upd = useUpdateThread(threadId);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  // Thread criada eager em draft mode (pela primeira mensagem OU pelo
  // primeiro anexo). Sem este state local, o sidepanel nao consegue
  // mostrar anexos da thread recem-criada porque a URL muda via
  // ``replaceState`` (sem disparar React Router/useLocation).
  //
  // IMPORTANTE: este ``useState`` precisa ficar AQUI, antes dos
  // early-returns ``if (threadQuery.isLoading) return ...``. Se vier
  // depois, o numero de hooks varia entre renders quando o threadQuery
  // transita de loading -> success, quebrando as regras dos hooks
  // ("Rendered more hooks than during the previous render").
  const [createdThreadId, setCreatedThreadId] = useState<string | null>(null);

  const { user: currentUser } = useAuth();
  const chatAgentId =
    !isDraft && threadQuery.data?.agent_id ? threadQuery.data.agent_id : null;
  const chatAgent = useAgent(chatAgentId ?? "");
  const canRate =
    !isDraft &&
    chatAgentId !== null &&
    threadQuery.data?.is_sandbox === false;
  const ratings = useAgentRatings(canRate ? chatAgentId : "");
  const myRating =
    ratings.data?.items.find((r) => r.user_id === currentUser?.id) ?? null;

  // Painel lateral: refresh metrics ao receber done; nao recebe TODOs
  // ao vivo no chat 1:1 (so team session).
  const [refreshKey, setRefreshKey] = useState(0);
  const [streaming, setStreaming] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const sidePanelCollapsed = useUiStore((s) => s.chatSidePanelCollapsed);
  const toggleSidePanel = useUiStore((s) => s.toggleChatSidePanel);

  const handleSseEvent = useCallback(
    (ev: { event: string; data: unknown }) => {
      // Sinaliza inicio de stream.
      if (ev.event === "agent_started" || ev.event === "token") {
        if (!streaming) setStreaming(true);
      }
      if (
        ev.event === "done" ||
        ev.event === "error" ||
        ev.event === "cancelled" ||
        ev.event === "message_completed"
      ) {
        setStreaming(false);
        // ``done`` e o sinal autoritativo de fim de turno -> refresh.
        if (ev.event === "done") {
          setRefreshKey((k) => k + 1);
        }
      }
    },
    [streaming],
  );


  if (!isDraft && threadQuery.isLoading) return <LoadingState />;
  if (!isDraft && threadQuery.isError)
    return <ErrorState error={threadQuery.error} />;
  if (!isDraft && !threadQuery.data) return null;

  const thread = threadQuery.data;

  // Conversa de TIME: renderiza o layout de time (diagrama, TODOs,
  // delegações) carregando o histórico desta thread -- antes caía no
  // layout 1:1. Conversas 1:1 (type=direct) seguem abaixo.
  if (!isDraft && thread?.type === "team" && thread.team_session_id) {
    return (
      <TeamChatView
        sessionId={thread.team_session_id}
        threadId={thread.id}
        backTo="/threads"
      />
    );
  }

  function openRename() {
    if (!thread) return;
    setTitleDraft(thread.title ?? "");
    setRenameOpen(true);
  }

  async function handleRename() {
    const next = titleDraft.trim();
    if (!next) {
      toast.error(t("rename.empty"));
      return;
    }
    try {
      await upd.mutateAsync({ title: next });
      toast.success(t("rename.success"));
      setRenameOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleDelete() {
    try {
      await del.mutateAsync(threadId);
      toast.success(t("delete.success"));
      navigate("/threads");
    } catch (err) {
      toastApiError(err);
      setDeleteOpen(false);
    }
  }

  const effectiveThreadId =
    createdThreadId ?? (isDraft ? "" : threadId);

  function handleThreadCreated(realThreadId: string) {
    // Atualiza a URL silenciosamente SEM passar pelo React Router. Usar
    // ``navigate(/threads/:id)`` aqui causa remount do ChatPage (a rota
    // muda de ``/threads/new`` para ``/threads/:threadId`` -- sao dois
    // route entries diferentes), o que mata a stream SSE em curso e o
    // usuario perde a primeira resposta do agente.
    //
    // ``window.history.replaceState`` muda a URL no historico do
    // browser sem disparar listeners do React Router, preservando o
    // componente montado e a stream ativa. No proximo F5/refresh, o
    // browser carrega ``/threads/:id`` e o React Router resolve para a
    // mesma ChatPage (agora em modo nao-draft).
    window.history.replaceState(null, "", `/threads/${realThreadId}`);
    setCreatedThreadId(realThreadId);
  }

  // -- Header ---------------------------------------------------------------
  const headerNode = isDraft ? (
    <header className="flex items-center justify-between gap-2 border-b border-line pb-3">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/threads")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex flex-col">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
            {t("draft.label")}
          </span>
          <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-ink-950">
            <Sparkles className="h-4 w-4 text-brand-500" />
            {draftAgent.data?.name ?? t("draft.selectAgent")}
          </h1>
        </div>
      </div>
    </header>
  ) : (
    <header className="flex items-center justify-between gap-2 border-b border-line pb-3">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/threads")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <button
            type="button"
            onClick={openRename}
            className="group inline-flex items-center gap-1 text-left"
            title={t("rename.tooltip")}
          >
            <h1 className="text-lg font-semibold tracking-tight text-ink-950">
              {thread?.title || t("chat.untitled")}
            </h1>
            <Pencil className="h-3 w-3 text-ink-500 opacity-0 transition-opacity group-hover:opacity-60" />
          </button>
          <div className="flex items-center gap-2">
            {thread?.is_sandbox ? (
              <Badge variant="outline" className="text-xs">
                {t("sandbox.badge")}
              </Badge>
            ) : null}
            <span className="text-xs text-ink-500">
              {thread ? new Date(thread.created_at).toLocaleString() : ""}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Sheet open={sidePanelOpen} onOpenChange={setSidePanelOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label={t("sidePanel.toggle")}
            >
              <Activity className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[320px] sm:max-w-sm">
            <SheetHeader>
              <SheetTitle>{t("sidePanel.title")}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 h-full">
              <ChatSidePanel
                threadId={effectiveThreadId}
                streaming={streaming}
                refreshKey={refreshKey}
              />
            </div>
          </SheetContent>
        </Sheet>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={openRename}>
            <Pencil className="h-4 w-4" />
            {t("actions.rename")}
          </DropdownMenuItem>
          {canRate ? (
            <DropdownMenuItem onClick={() => setRateOpen(true)}>
              <Star className="h-4 w-4" />
              {myRating ? t("actions.editRating") : t("actions.rate")}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            className="text-danger-700 focus:bg-danger-50 focus:text-danger-700"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
            {t("actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </header>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {headerNode}

      <div className="flex min-h-0 flex-1 gap-3">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {isDraft ? (
            <ChatStream
              threadId=""
              draftAgentId={draftAgentId}
              onThreadCreated={handleThreadCreated}
              onSseEvent={handleSseEvent}
            />
          ) : (
            <ChatStream
              threadId={threadId}
              onSseEvent={handleSseEvent}
            />
          )}
        </div>
        <ChatSidePanel
          threadId={effectiveThreadId}
          streaming={streaming}
          refreshKey={refreshKey}
          collapsed={sidePanelCollapsed}
          onToggleCollapsed={toggleSidePanel}
          className={cn(
            "hidden shrink-0 lg:flex",
            sidePanelCollapsed
              ? "lg:w-[56px]"
              : "lg:w-[320px] xl:w-[360px]",
          )}
        />
      </div>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("rename.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="thread-title">{t("rename.label")}</Label>
            <Input
              id="thread-title"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              maxLength={255}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleRename();
                }
              }}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setRenameOpen(false)}
                disabled={upd.isPending}
              >
                {tc("actions.cancel")}
              </Button>
              <Button onClick={handleRename} disabled={upd.isPending}>
                {tc("actions.save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {canRate && chatAgentId ? (
        <RateAgentDialog
          agentId={chatAgentId}
          agentName={chatAgent.data?.name ?? "agente"}
          open={rateOpen}
          onOpenChange={setRateOpen}
          existingRating={myRating}
        />
      ) : null}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>
              {tc("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={del.isPending}
              className="bg-danger-500 from-danger-500 via-danger-500 to-danger-700 text-white hover:bg-danger-700"
            >
              {del.isPending ? t("delete.pending") : t("delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
